import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { ClipboardList, BarChart3, Eye , Download} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { tareasDespachoAPI, usuariosAPI } from '../api';
import { toast } from '../hooks/use-toast';
import Cobrettis from './Cobrettis';
import HonorariosTab from '../components/HonorariosTab';

import { CATEGORIAS_CONFIG } from '../components/tareas/constants';
import TareaAbogadosTab from '../components/tareas/TareaAbogadosTab';
import TareaMinutasTab from '../components/tareas/TareaMinutasTab';
import TareaCostasTab from '../components/tareas/TareaCostasTab';
import TareaEjecucionesTab from '../components/tareas/TareaEjecucionesTab';
import TareaPastelitosTab from '../components/tareas/TareaPastelitosTab';
import CitaAbogadosTab from '../components/tareas/CitaAbogadosTab';
import AtencionClienteTab from '../components/tareas/AtencionClienteTab';
import PropuestasInteresesTab from '../components/tareas/PropuestasInteresesTab';
import Ejecuciones from './Ejecuciones';

const TareasDespacho = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'abogados');
  // __V68__ sync activeTab cuando cambia ?tab=… (deeplinks de Ejecuciones)
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && t !== activeTab) setActiveTab(t);
  // eslint-disable-next-line
  }, [searchParams]);

  const [showDialog, setShowDialog] = useState(false);
  const [tareaDialog, setTareaDialog] = useState(null);
  const [tareas, setTareas] = useState([]);
  const [stats, setStats] = useState({ por_categoria: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCompletada, setFilterCompletada] = useState('pendientes');
  const [filterPrioridad, setFilterPrioridad] = useState('');
  
  const [usuarios, setUsuarios] = useState([]);
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [editingTarea, setEditingTarea] = useState(null);
  const [selectedTarea, setSelectedTarea] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadTareas();
  }, [activeTab, filterCompletada, filterPrioridad]);

  const loadInitialData = async () => {
    try {
      const [statsRes, usuariosRes] = await Promise.all([
        tareasDespachoAPI.getStats(),
        usuariosAPI.getAll()
      ]);
      setStats(statsRes.data);
      setUsuarios(usuariosRes.data);
    } catch (error) {
      console.error('Error cargando datos iniciales:', error);
    }
  };

  const loadTareas = async () => {
    try {
      setLoading(true);
      const params = { search: search || undefined };
      if (filterCompletada === 'pendientes') {
        params.completada = false;
      } else if (filterCompletada === 'completadas') {
        params.completada = true;
      }
      if (filterPrioridad) {
        params.prioridad = filterPrioridad;
      }
      let tareasRes;
      switch (activeTab) {
        case 'abogados':
          tareasRes = await tareasDespachoAPI.abogados.getAll(params);
          break;
        case 'minutas':
          if (filterCompletada === 'pendientes') {
            delete params.completada;
          }
          tareasRes = await tareasDespachoAPI.minutas.getAll(params);
          break;
        case 'costas_pagar':
          tareasRes = await tareasDespachoAPI.costas.getAll(params);
          break;
        case 'ejecuciones':
          tareasRes = await tareasDespachoAPI.ejecuciones.getAll(params);
          break;
        case 'pastelitos':
          tareasRes = await tareasDespachoAPI.pastelitos.getAll(params);
          break;
        default:
          tareasRes = { data: [] };
      }
      setTareas(tareasRes.data);
      const statsRes = await tareasDespachoAPI.getStats();
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error cargando tareas:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar las tareas', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tarea) => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    console.log('Eliminando tarea:', { activeTab, tareaId: tarea.id, tarea });
    try {
      switch (activeTab) {
        case 'abogados':
          await tareasDespachoAPI.abogados.delete(tarea.id);
          break;
        case 'minutas':
          await tareasDespachoAPI.minutas.delete(tarea.id);
          break;
        case 'costas_pagar':
          await tareasDespachoAPI.costas.delete(tarea.id);
          break;
        case 'ejecuciones':
          await tareasDespachoAPI.ejecuciones.delete(tarea.id);
          break;
        case 'pastelitos':
          await tareasDespachoAPI.pastelitos.delete(tarea.id);
          break;
        default:
          console.error('Tab no reconocido:', activeTab);
          throw new Error(`Tab no reconocido: ${activeTab}`);
      }
      toast({ title: 'Tarea eliminada' });
      loadTareas();
    } catch (error) {
      console.error('Error eliminando tarea:', error);
      const mensaje = error.response?.data?.detail || error.message || 'No se pudo eliminar';
      toast({ title: 'Error', description: mensaje, variant: 'destructive' });
    }
  };

  const handleToggleCompletada = async (tarea) => {
    console.log('Toggle completada:', { activeTab, tareaId: tarea.id, completada: tarea.completada, expediente_id: tarea.expediente_id });
    try {
      const nuevoEstado = !tarea.completada;
      if ((activeTab === 'abogados' || activeTab === 'pastelitos') && nuevoEstado && tarea.expediente_id) {
        setTareaDialog({...tarea, nuevoEstado});
        setShowDialog(true);
        return;
      }
      switch (activeTab) {
        case 'abogados':
          await tareasDespachoAPI.abogados.completar(tarea.id, nuevoEstado);
          break;
        case 'costas_pagar':
          await tareasDespachoAPI.costas.completar(tarea.id, nuevoEstado);
          break;
        case 'ejecuciones':
          await tareasDespachoAPI.ejecuciones.completar(tarea.id, nuevoEstado);
          break;
        case 'pastelitos':
          await tareasDespachoAPI.pastelitos.completar(tarea.id, nuevoEstado);
          break;
        default:
          console.error('Tab no reconocido para completar:', activeTab);
          throw new Error(`Tab no reconocido: ${activeTab}`);
      }
      toast({ title: nuevoEstado ? 'Tarea completada' : 'Tarea marcada como pendiente' });
      loadTareas();
    } catch (error) {
      console.error('Error toggle completada:', error);
      const mensaje = error.response?.data?.detail || error.message || 'Error al actualizar';
      toast({ title: 'Error', description: mensaje, variant: 'destructive' });
    }
  };

  const handleDialogActuacion = async (crear) => {
    if (!tareaDialog) return;
    try {
      const nuevoEstado = tareaDialog.nuevoEstado;
      if (activeTab === 'abogados') await tareasDespachoAPI.abogados.completar(tareaDialog.id, nuevoEstado);
      else if (activeTab === 'pastelitos') await tareasDespachoAPI.pastelitos.completar(tareaDialog.id, nuevoEstado);
      if (crear) {
        const api = activeTab === 'abogados' ? tareasDespachoAPI.abogados : tareasDespachoAPI.pastelitos;
        await api.crearActuacion(tareaDialog.id);
        toast({ title: 'Actuacion creada correctamente' });
      }
      toast({ title: 'Tarea completada' });
      loadTareas();
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
    finally { setShowDialog(false); setTareaDialog(null); }
  };

  const handleExportExcelIntereses_v52 = async () => {
    try {
      const token = localStorage.getItem('lexduo_token');
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/tareas-despacho/minutas/intereses/export-excel?estado_propuesta=todas`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Error en descarga');
      const blob = await r.blob();
      const cd = r.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename = m ? m[1] : `propuestas_intereses_${new Date().toISOString().slice(0,10)}.xlsx`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (e) {
      console.error('Error Excel intereses', e);
      alert('Error descargando Excel');
    }
  };
  const currentConfig = CATEGORIAS_CONFIG[activeTab];
  const CurrentIcon = currentConfig.icon;
  const categoriaStats = stats.por_categoria?.find(c => c.categoria === activeTab) || { total: 0, pendientes: 0 };

  return (
    <div className="p-6 space-y-6">
      {activeTab === 'propuestas_intereses' && (
        <div className="flex justify-end -mb-3">
          <button
            type="button"
            onClick={handleExportExcelIntereses_v52}
            data-testid="export-excel-propuestas-intereses-btn"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border rounded-md hover:bg-gray-50 shadow-sm text-gray-700"
          >
            <Download className="w-4 h-4" />
            Descargar Excel
          </button>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tareas del Despacho</h1>
            <p className="text-gray-600">Gestión de tareas por categoría</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => window.location.href = '/tareas/estadisticas'} className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Ver Estadísticas
          </Button>
          {(user?.rol === 'admin' || (user?.permisos?.['tareas-supervision'] && (typeof user.permisos['tareas-supervision'] === 'boolean' ? user.permisos['tareas-supervision'] : user.permisos['tareas-supervision']?.lectura))) && (
            <Button 
              variant="outline" 
              onClick={() => window.location.href = '/tareas/supervision'}
              className="gap-2 text-blue-700 border-blue-200 hover:bg-blue-50"
              data-testid="btn-supervision"
            >
              <Eye className="w-4 h-4" />
              Supervision
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Object.values(CATEGORIAS_CONFIG).map((cat) => {
          const catStats = stats.por_categoria?.find(c => c.categoria === cat.id);
          const Icon = cat.icon;
          return (
            <Card key={cat.id} className={`cursor-pointer transition-all hover:shadow-md border-l-4 ${cat.borderColor} ${activeTab === cat.id ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`} onClick={() => setActiveTab(cat.id)}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-5 h-5 ${cat.textColor}`} />
                  <span className="text-sm font-medium text-gray-700">{cat.nombre}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold">{catStats?.pendientes || 0}</span>
                  <span className="text-sm text-gray-500">/ {catStats?.total || 0}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-9 h-auto">
          {Object.values(CATEGORIAS_CONFIG).map((cat) => {
            const Icon = cat.icon;
            const catStats = stats.por_categoria?.find(c => c.categoria === cat.id);
            return (
              <TabsTrigger key={cat.id} value={cat.id} className="flex items-center gap-2 py-3">
                <Icon className="w-4 h-4" />
                <span className="hidden lg:inline">{cat.nombre}</span>
                {catStats?.pendientes > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs">{catStats.pendientes}</Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="abogados" className="mt-4 space-y-4">
          <TareaAbogadosTab tareas={tareas} loading={loading} search={search} setSearch={setSearch} filterCompletada={filterCompletada} setFilterCompletada={setFilterCompletada} filterPrioridad={filterPrioridad} setFilterPrioridad={setFilterPrioridad} loadTareas={loadTareas} handleDelete={handleDelete} handleToggleCompletada={handleToggleCompletada} usuarios={usuarios} config={CATEGORIAS_CONFIG.abogados} />
        </TabsContent>

        <TabsContent value="minutas" className="mt-4 space-y-4">
          <TareaMinutasTab tareas={tareas} loading={loading} search={search} setSearch={setSearch} filterCompletada={filterCompletada} setFilterCompletada={setFilterCompletada} filterPrioridad={filterPrioridad} setFilterPrioridad={setFilterPrioridad} loadTareas={loadTareas} handleDelete={handleDelete} usuarios={usuarios} config={CATEGORIAS_CONFIG.minutas} />
        </TabsContent>

        <TabsContent value="costas_pagar" className="mt-4 space-y-4">
          <TareaCostasTab tareas={tareas} loading={loading} search={search} setSearch={setSearch} filterCompletada={filterCompletada} setFilterCompletada={setFilterCompletada} filterPrioridad={filterPrioridad} setFilterPrioridad={setFilterPrioridad} loadTareas={loadTareas} handleDelete={handleDelete} handleToggleCompletada={handleToggleCompletada} usuarios={usuarios} config={CATEGORIAS_CONFIG.costas_pagar} />
        </TabsContent>

        <TabsContent value="ejecuciones" className="mt-4 space-y-4">
          <Ejecuciones embedded={true} />
        </TabsContent>

        <TabsContent value="pastelitos" className="mt-4 space-y-4">
          <TareaPastelitosTab tareas={tareas} loading={loading} search={search} setSearch={setSearch} filterCompletada={filterCompletada} setFilterCompletada={setFilterCompletada} filterPrioridad={filterPrioridad} setFilterPrioridad={setFilterPrioridad} loadTareas={loadTareas} handleDelete={handleDelete} handleToggleCompletada={handleToggleCompletada} usuarios={usuarios} config={CATEGORIAS_CONFIG.pastelitos} />
        </TabsContent>

        <TabsContent value="citas_abogados" className="mt-4 space-y-4">
          <CitaAbogadosTab usuarios={usuarios} config={CATEGORIAS_CONFIG.citas_abogados} />
        </TabsContent>

        <TabsContent value="atencion_cliente" className="mt-4 space-y-4">
          <AtencionClienteTab usuarios={usuarios} config={CATEGORIAS_CONFIG.atencion_cliente} />
        </TabsContent>
        
        <TabsContent value="propuestas_intereses" className="mt-4 space-y-4">
          <PropuestasInteresesTab config={CATEGORIAS_CONFIG.propuestas_intereses} />
        </TabsContent>
        <TabsContent value="cobrettis" className="mt-4">
          <Cobrettis />
        </TabsContent>
        <TabsContent value="honorarios" className="mt-4">
          <HonorariosTab />
        </TabsContent>
      </Tabs>
      {showDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-4">Crear Actuacion</h3>
            <p className="text-gray-600 mb-6">Deseas crear una actuacion en el expediente?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => handleDialogActuacion(false)} className="px-4 py-2 border rounded-md hover:bg-gray-100">No, solo completar</button>
              <button onClick={() => handleDialogActuacion(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Si, crear actuacion</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TareasDespacho;
